import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    FruitfulCustomIcon,
    XStack,
    YStack,
} from 'smoothie';

import { formatDollars } from '../../../../common/utils/bankingUtils';
import type { BankAccountDisplayDetail } from '../../../types/BankAccount';
import {
    type PaycheckRoutingRule,
    type PaycheckRoutingRuleAccountMetadata,
    PaycheckRoutingRuleAccountType,
    PaycheckRoutingRuleType,
} from '../../../types/PaycheckRouting';

const getRuleDescription = (rule: PaycheckRoutingRule) => {
    const { type, amount } = rule;
    const formattedAmount =
        type === PaycheckRoutingRuleType.PERCENTAGE
            ? `${amount}%`
            : formatDollars(amount);

    switch (type) {
        case PaycheckRoutingRuleType.FIXED:
            return `Transfer ${formattedAmount}`;
        case PaycheckRoutingRuleType.MINIMUM_BALANCE:
            return `Replenish ${formattedAmount} balance`;
        default:
            return formattedAmount;
    }
};

interface RuleSummaryProps {
    account: BankAccountDisplayDetail | PaycheckRoutingRuleAccountMetadata;
    rule: PaycheckRoutingRule;
    onRemove?: () => void;
    deemphasize?: boolean;
    needsAttention?: boolean;
}

const RuleSummary = ({
    account,
    rule,
    deemphasize,
    needsAttention,
}: RuleSummaryProps) => {
    const { icon, color, nickname } = account;

    return (
        <XStack justifyContent="space-between" alignItems="center">
            <XStack gap={8} alignItems="center">
                <FruitfulCustomIcon
                    icon={icon}
                    color={color}
                    size="md"
                    useAltColors={
                        rule.accountType ===
                        PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
                    }
                />
                <YStack gap={4}>
                    <BodyMediumEmphasized
                        color={
                            needsAttention
                                ? '$gray800'
                                : deemphasize
                                  ? '$gray1000'
                                  : '$black'
                        }
                    >
                        {getRuleDescription(rule)}
                    </BodyMediumEmphasized>
                    <BodyMediumRegular
                        color={needsAttention ? '$gray800' : '$gray1000'}
                    >
                        {nickname}
                    </BodyMediumRegular>
                </YStack>
            </XStack>
        </XStack>
    );
};

export default RuleSummary;
